<template>
  <div class="fr-matches-container">
    <div class="fr-matches-list" ref="scrollContainer">
      <div
        v-for="(value, key) in frMatches"
        :key="`fr-item-${key}`"
        class="fr-match-item"
        :ref="`item-${key}`"
      >
        <div class="loading-wrapper" v-if="value == ''">
          <b-spinner variant="primary" class="loading-spinner"></b-spinner>
        </div>
        <fr-data
          v-else
          :key="`fr-data-${key}-${value.created}`"
          :dataMatch="value"
          :frData="frData"
          :index="key"
          :isLastOne="key === frMatches.length - 1"
        />
      </div>
    </div>
  </div>
</template>
<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import FRData from "./FRData.vue";
import axios from "@axios";
import { BButton } from "bootstrap-vue";

export default {
  components: {
    "fr-data": FRData,
    BButton,
  },
  props: {
    frData: Object,
  },
  data() {
    return {
      frMatches: [],
      observer: null,
      loadedItems: new Set(),
    };
  },
  methods: {
    showMessage(title, msg, variant = "success") {
      this.$toast({
        component: ToastificationContent,
        position: "top-right",
        props: {
          title,
          variant,
          icon: "BellIcon",
          text: msg,
          timeout: 5000,
        },
      });
    },
    async loadFRMatch(index) {
      if (this.loadedItems.has(index) || this.frMatches[index] !== "") return;

      if (!this.$props.frData || !this.$props.frData.alarm_id) {
        console.warn("frData or alarm_id is missing");
        return;
      }

      this.loadedItems.add(index);

      try {
        const response = await axios.post(
          `${window.__env.VUE_APP_BASE_URL}/v1/luna/getAlarmDataFRMatchByIndex`,
          {
            alarmId: this.$props.frData.alarm_id,
            index: index + 1,
          }
        );
        const snapshot = response.data.data;
        this.$set(this.frMatches, index, snapshot);
      } catch (error) {
        console.error("Error loading FR match:", error);
        this.loadedItems.delete(index);
      }
    },
    setupIntersectionObserver() {
      if (!("IntersectionObserver" in window)) {
        for (let i = 1; i < this.frMatches.length; i++) {
          this.loadFRMatch(i);
        }
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = parseInt(entry.target.dataset.index);
              if (!isNaN(index)) {
                this.loadFRMatch(index);
              }
            }
          });
        },
        {
          root: this.$refs.scrollContainer,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      this.$nextTick(() => {
        for (let i = 1; i < this.frMatches.length; i++) {
          const element = this.$refs[`item-${i}`];
          if (element && element[0]) {
            element[0].dataset.index = i;
            this.observer.observe(element[0]);
          }
        }
      });
    },
  },

  async mounted() {
    if (!this.$props.frData || !this.$props.frData.alarm_id) {
      this.$emit("has-fr-data", false);
      return;
    }

    window.Echo.private(`fr-location-alarm-${this.$props.frData.alarm_id}`).listen(
      ".create",
      ({ message }) => {
        this.frMatches.push(message);
      }
    );

    try {
      const { data } = await axios.post(
        `${window.__env.VUE_APP_BASE_URL}/v1/luna/getAlarmDataFRFirstMatch`,
        {
          alarmId: this.$props.frData.alarm_id,
        }
      );
      const snapshot = data.data;
      const totalMatches = snapshot.total_events;

      if (totalMatches > 0) {
        this.$set(this.frMatches, 0, snapshot);
        this.loadedItems.add(0);

        for (let index = 1; index < totalMatches; index++) {
          this.frMatches[index] = "";
        }

        this.$emit("has-fr-data", true);

        if (totalMatches > 1) {
          this.setupIntersectionObserver();
        }
      } else {
        this.$emit("has-fr-data", false);
      }
    } catch (error) {
      console.error("Error loading FR data:", error);
      this.$emit("has-fr-data", false);
    }
  },
  beforeDestroy() {
    if (this.$props.frData && this.$props.frData.alarm_id) {
      window.Echo.leave(`fr-location-alarm-${this.$props.frData.alarm_id}`);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>
<style scoped>
.fr-matches-container {
  width: 100%;
}

.fr-matches-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
}

.fr-match-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.fr-match-item:last-child {
  margin-bottom: 0;
}

.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.loading-spinner {
  margin-top: 20px;
}

.dark-layout .fr-match-item {
  background-color: var(--main-dark);
}
</style>
